import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { useAuthStore } from 'store/authStore';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAuthStore((s) => s.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const sessionExpired = searchParams.get('reason') === 'session_expired';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const ok = await login(email.trim(), password);
      if (ok) {
        navigate('/scenarios');
      } else {
        setError('Неверный email или пароль.');
      }
    } catch {
      setError('Ошибка соединения с сервером.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 380 }}>
        {/* Logo above card */}
        <Box sx={{ textAlign: 'center', mb: 3.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '13px',
              background: 'linear-gradient(135deg, #1474FF 0%, #3B8BFF 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1.5,
              boxShadow: '0 4px 16px rgba(20,116,255,0.38)',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <circle cx="11" cy="11" r="4" fill="white" fillOpacity="0.95" />
              <circle cx="11" cy="3.5" r="2" fill="white" fillOpacity="0.5" />
              <circle cx="11" cy="18.5" r="2" fill="white" fillOpacity="0.5" />
              <circle cx="3.5" cy="11" r="2" fill="white" fillOpacity="0.5" />
              <circle cx="18.5" cy="11" r="2" fill="white" fillOpacity="0.5" />
            </svg>
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 18,
              letterSpacing: '-0.5px',
              color: 'var(--color-text-primary)',
            }}
          >
            Globus
            <Box component="span" sx={{ color: 'var(--color-primary)' }}>
              {' '}
              AI
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', mt: 0.25 }}>
            Корпоративное рабочее пространство
          </Typography>
        </Box>

        {/* Card */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            background: '#FFFFFF',
            border: '1px solid rgba(20,116,255,0.12)',
            borderRadius: 'var(--radius-xl)',
            p: '36px',
            boxShadow: '0 8px 40px rgba(20,116,255,0.12), 0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 16,
              letterSpacing: '-0.3px',
              mb: 0.5,
              color: 'var(--color-text-primary)',
            }}
          >
            Вход в систему
          </Typography>
          <Typography sx={{ color: 'var(--color-text-muted)', fontSize: 13, mb: 3 }}>
            Введите корпоративные данные
          </Typography>

          {sessionExpired && !error && (
            <Alert
              severity="warning"
              sx={{
                mb: 2.5,
                fontSize: 12.5,
                borderRadius: 'var(--radius-md)',
                border: '1px solid rgba(234,179,8,0.25)',
                '& .MuiAlert-icon': { fontSize: 16 },
              }}
            >
              Сессия истекла. Войдите снова.
            </Alert>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5,
                fontSize: 12.5,
                borderRadius: 'var(--radius-md)',
                border: '1px solid rgba(239,68,68,0.2)',
                bgcolor: 'var(--color-error-tint)',
                '& .MuiAlert-icon': { fontSize: 16 },
              }}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              size="small"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              InputLabelProps={{ style: { fontSize: 13.5 } }}
            />
            <TextField
              label="Пароль"
              type="password"
              fullWidth
              size="small"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              InputLabelProps={{ style: { fontSize: 13.5 } }}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              mt: 3,
              py: 1.375,
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: '-0.01em',
              borderRadius: 'var(--radius-md)',
              background: 'linear-gradient(135deg, #1474FF 0%, #3B8BFF 100%)',
              boxShadow: '0 2px 8px rgba(20,116,255,0.30)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0F5FE0 0%, #1474FF 100%)',
                boxShadow: '0 4px 16px rgba(20,116,255,0.40)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Войти'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
